import { HttpParams, HttpResourceRequest } from '@angular/common/http';
import { Injector, runInInjectionContext } from '@angular/core';
import {
  EShiftPhrase,
  IDateRangePickerDate,
  IPersistentBoard,
  noCategoryId,
  specialCommentTags,
  widgetHttpHeader,
} from 'scw-digital-boards-widgets';
import { HttpResourceWithTrigger } from '../../../core/classes/http-resource-with-trigger';
import { actionTrackerContext, oeeContext } from '../../../core/interceptors/api-interceptor.interceptor';
import { ETranslatableEntity } from '../../../core/services/entity-translator-service';
import {
  IActivity,
  IItemCategory,
  IJob,
  ILine,
  ISite,
  ITask,
  TWorkflowStep,
} from '../../../pages/dashboard-content/widget-add-edit-dialog/widget-add-edit-dialog.interface';
import { IApiCustomFilterConfiguration } from '../../../pages/settings/filter-settings/custom-filters/custom-filters.interface';
import { DateUtilitiesService } from '../../../utilities/date-utilities.service';
import { IShift, IWorkOrder } from '../form-association/form-association.interface';
import { IDropdownOption } from '../mat-dropdown/mat-dropdown.component';

export const lineApiResource = (injector: Injector) =>
  runInInjectionContext(
    injector,
    () =>
      new HttpResourceWithTrigger<ILine>({
        dependencies: ['siteIds'],
        formatter: (data: ILine[]): IDropdownOption[] => data.map((line) => ({ id: line.id, name: line.title })),
        httpResourceRequest: {
          context: oeeContext,
          headers: widgetHttpHeader,
          method: 'GET',
          reportProgress: true,
          url: 'lines',
        },
        paramsUpdater: (request, { httpUtilities }, { dependencyParameters }): HttpResourceRequest => {
          return {
            ...request,
            params: httpUtilities.insertGenericCrudRequestParameters({
              filters: [
                ...(dependencyParameters.siteIds ? [{ field: 'siteId', ids: dependencyParameters.siteIds }] : []),
              ],
              limit: 1000,
            }),
          };
        },
      }),
  );

export const boardApiResource = (injector: Injector) =>
  runInInjectionContext(
    injector,
    () =>
      new HttpResourceWithTrigger<IPersistentBoard>({
        dependencies: [],
        formatter: (data: IPersistentBoard[]): IPersistentBoard[] => data,
        httpResourceRequest: {
          context: actionTrackerContext,
          headers: widgetHttpHeader,
          method: 'GET',
          params: new HttpParams().set('limit', 1000),
          reportProgress: true,
          url: 'boards',
        },
      }),
  );

export const itemCategoryApiResource = (injector: Injector) =>
  runInInjectionContext(
    injector,
    () =>
      new HttpResourceWithTrigger<IItemCategory>({
        dependencies: [],
        formatter: (data: IItemCategory[], { translate }): IDropdownOption[] => [
          {
            id: noCategoryId,
            name: translate.instant('widget.sqcdpMonthly.sqcdpType.noCategory'),
          },
          ...data.map((itemCategory) => {
            const defaultOptions = [
              {
                id: 'safety',
                name: translate.instant('widget.sqcdpMonthly.sqcdpType.safety'),
              },
              {
                id: 'quality',
                name: translate.instant('widget.sqcdpMonthly.sqcdpType.quality'),
              },
              {
                id: 'cost',
                name: translate.instant('widget.sqcdpMonthly.sqcdpType.cost'),
              },
              {
                id: 'delivery',
                name: translate.instant('widget.sqcdpMonthly.sqcdpType.delivery'),
              },
              {
                id: 'people',
                name: translate.instant('widget.sqcdpMonthly.sqcdpType.people'),
              },
            ];

            return {
              id: itemCategory.id,
              name: !itemCategory.isDefault
                ? itemCategory.name
                : (defaultOptions.find((option) => option.id === itemCategory.name)?.name ?? ''),
            };
          }),
        ],
        httpResourceRequest: {
          context: actionTrackerContext,
          headers: widgetHttpHeader,
          method: 'GET',
          params: new HttpParams().set('limit', 1000),
          reportProgress: true,
          url: 'item-categories',
        },
      }),
  );

export const workflowStepApiResource = (injector: Injector) =>
  runInInjectionContext(
    injector,
    () =>
      new HttpResourceWithTrigger<TWorkflowStep>({
        dependencies: ['statusCategory'],
        formatter: (data: TWorkflowStep[], { translate }): IDropdownOption[] =>
          data.map((workflowStep) => ({
            id: workflowStep.id,
            name: workflowStep.isDefault
              ? translate.instant(`general.itemStatus.${workflowStep.name}`)
              : workflowStep.name,
          })),
        httpResourceRequest: {
          context: actionTrackerContext,
          headers: widgetHttpHeader,
          method: 'GET',
          reportProgress: true,
          url: 'workflow-steps',
        },
        paramsUpdater: (request, { httpUtilities }, { dependencyParameters }): HttpResourceRequest => {
          return {
            ...request,
            params: httpUtilities.insertGenericCrudRequestParameters({
              filters: [
                ...(dependencyParameters.statusCategory?.length
                  ? [{ field: 'statusCategory', ids: dependencyParameters.statusCategory }]
                  : []),
              ],
              limit: 1000,
            }),
          };
        },
      }),
  );

export const siteApiResource = (injector: Injector) =>
  runInInjectionContext(
    injector,
    () =>
      new HttpResourceWithTrigger<ISite>({
        dependencies: [],
        formatter: (data: ISite[]): IDropdownOption[] => data.map((site) => ({ id: site.id, name: site.name })),
        httpResourceRequest: {
          context: oeeContext,
          headers: widgetHttpHeader,
          method: 'GET',
          params: new HttpParams().set('limit', 1000),
          reportProgress: true,
          url: 'sites',
        },
      }),
  );

export const jobApiResource = (injector: Injector) =>
  runInInjectionContext(
    injector,
    () =>
      new HttpResourceWithTrigger<IJob>({
        dependencies: [],
        formatter: (data: IJob[]): IDropdownOption[] => data.map((job) => ({ id: job.id, name: job.jobName })),
        httpResourceRequest: {
          context: oeeContext,
          headers: widgetHttpHeader,
          method: 'GET',
          reportProgress: true,
          url: 'jobs',
        },
        paramsUpdater: (request, { httpUtilities }, { manualParameters }): HttpResourceRequest => {
          return {
            ...request,
            params: httpUtilities.insertGenericCrudRequestParameters({
              limit: 1000,
              search: { searchedFields: ['jobName'], searchText: manualParameters.search },
            }),
          };
        },
      }),
  );

export const shiftApiResource = (injector: Injector) =>
  runInInjectionContext(
    injector,
    () =>
      new HttpResourceWithTrigger<IShift>({
        dependencies: ['siteIds', 'lineId', 'date'],
        formatter: (data: IShift[], { translate }): IDropdownOption[] => {
          return [
            { id: EShiftPhrase.currentShift, name: translate.instant('general.currentShift') },
            { id: EShiftPhrase.previousShift, name: translate.instant('general.previousShift') },
            ...data.map((shift) => ({ id: shift.id, name: shift.name })),
          ];
        },
        httpResourceRequest: {
          context: oeeContext,
          headers: widgetHttpHeader,
          method: 'GET',
          params: new HttpParams().set('limit', 1000),
          reportProgress: true,
          url: 'scheduler/shifts/shift-details',
        },
        paramsUpdater: (request, _, { dependencyParameters }): HttpResourceRequest => {
          let params = new HttpParams();

          if ('lineId' in dependencyParameters) {
            params = params.set('lineIds', `[${dependencyParameters.lineId.join(',')}]`);
          }

          if ('siteIds' in dependencyParameters) {
            params = params.set('siteIds', `[${dependencyParameters.siteIds.join(',')}]`);
          }

          const isRangeDateFilterDefined =
            'date' in dependencyParameters && typeof dependencyParameters.date[0] !== 'string';

          if ('lineId' in dependencyParameters || isRangeDateFilterDefined) {
            const start = isRangeDateFilterDefined
              ? (dependencyParameters.date[0] as IDateRangePickerDate<string>).startDate
              : DateUtilitiesService.convertUserDateToUTCString(DateUtilitiesService.getNowInUserTz().startOf('y'));
            const end = isRangeDateFilterDefined
              ? (dependencyParameters.date[0] as IDateRangePickerDate<string>).endDate
              : DateUtilitiesService.convertUserDateToUTCString(DateUtilitiesService.getNowInUserTz());

            params = params.set('dateRange', `["${start}","${end}"]`);
          }

          return {
            ...request,
            params,
          };
        },
      }),
  );

export const woApiResource = (injector: Injector) =>
  runInInjectionContext(
    injector,
    () =>
      new HttpResourceWithTrigger<IWorkOrder>({
        dependencies: ['siteIds'],
        formatter: (data: IWorkOrder[]): IDropdownOption[] => data.map((wo) => ({ id: wo.id, name: wo.woNumber })),
        httpResourceRequest: {
          context: oeeContext,
          headers: widgetHttpHeader,
          method: 'GET',
          url: 'work-orders',
        },
        paramsUpdater: (
          request,
          { httpUtilities },
          { dependencyParameters, manualParameters },
        ): HttpResourceRequest => {
          return {
            ...request,
            params: httpUtilities.insertGenericCrudRequestParameters({
              ...(manualParameters.search
                ? { search: { searchedFields: ['woNumber'], searchText: manualParameters.search } }
                : {}),
              filters: [
                ...(dependencyParameters.siteIds ? [{ field: 'siteId', ids: dependencyParameters.siteIds }] : []),
              ],
              limit: 20,
            }),
          };
        },
        paramsUpdaterForInitialValues: (request, { httpUtilities }, { manualParameters }): HttpResourceRequest => {
          return {
            ...request,
            params: httpUtilities.insertGenericCrudRequestParameters({
              filters: [{ field: 'id', ids: manualParameters.initialIds! }],
              limit: 1000,
            }),
          };
        },
      }),
  );

export const activityApiResource = (injector: Injector) =>
  runInInjectionContext(
    injector,
    () =>
      new HttpResourceWithTrigger<IActivity>({
        dependencies: ['lineId'],
        formatter: (data, { entityTranslatorService }): IDropdownOption[] => {
          return data.map(({ id, name, entityTranslations }) => {
            const translatedTitle = entityTranslatorService.findTranslatedValue(
              entityTranslations,
              ETranslatableEntity.activities,
              'name',
              id,
              name,
            );

            return { id, name: translatedTitle };
          });
        },
        httpResourceRequest: {
          context: oeeContext,
          headers: widgetHttpHeader,
          method: 'GET',
          url: 'activities',
        },
        paramsUpdater: (
          request,
          { httpUtilities },
          { dependencyParameters, manualParameters },
        ): HttpResourceRequest => {
          const params = httpUtilities.insertGenericCrudRequestParameters({
            fields: ['id', 'name'],
            ...(manualParameters.search
              ? { search: { searchedFields: ['name'], searchText: manualParameters.search } }
              : {}),
            limit: 20,
          });

          return {
            ...request,
            params: dependencyParameters.lineId ? params.append('lineId', dependencyParameters.lineId[0]) : params,
          };
        },
        paramsUpdaterForInitialValues: (request, { httpUtilities }, { manualParameters }): HttpResourceRequest => {
          return {
            ...request,
            params: httpUtilities.insertGenericCrudRequestParameters({
              filters: [{ field: 'id', ids: manualParameters.initialIds! }],
              limit: 1000,
            }),
          };
        },
      }),
  );

// noinspection D
export const taskNameApiResource = (injector: Injector) =>
  runInInjectionContext(
    injector,
    () =>
      new HttpResourceWithTrigger<ITask>({
        dependencies: ['siteIds', 'lineId', 'activityId'],
        formatter: (data, { entityTranslatorService }): IDropdownOption<string>[] =>
          data.map((task) => {
            const translatedTitle = entityTranslatorService.findTranslatedValue(
              task.entityTranslations,
              ETranslatableEntity.tasks,
              'title',
              task.id,
              task.title,
            );

            return { id: translatedTitle, name: translatedTitle };
          }),
        httpResourceRequest: {
          context: oeeContext,
          headers: { ...widgetHttpHeader, 'X-HTTP-Method': 'GET' },
          method: 'POST',
          url: 'tasks',
        },
        paramsUpdater: (
          request,
          { httpUtilities },
          { dependencyParameters, manualParameters },
        ): HttpResourceRequest => {
          return {
            ...request,
            params: httpUtilities.insertGenericCrudRequestParameters({
              ...(manualParameters.search
                ? { search: { searchedFields: ['title'], searchText: manualParameters.search } }
                : {}),
              filters: [
                ...(dependencyParameters.siteIds ? [{ field: 'siteId', ids: dependencyParameters.siteIds }] : []),
                ...(dependencyParameters.lineId ? [{ field: 'lineId', ids: dependencyParameters.lineId }] : []),
                ...(dependencyParameters.activityId
                  ? [{ field: 'activityId', ids: dependencyParameters.activityId }]
                  : []),
              ],
              limit: 500,
            }),
          };
        },
        paramsUpdaterForInitialValues: (request, { httpUtilities }, { manualParameters }): HttpResourceRequest => {
          return {
            ...request,
            params: httpUtilities.insertGenericCrudRequestParameters({
              filters: [{ field: 'title', ids: manualParameters.initialIds! }],
              limit: 1000,
            }),
          };
        },
      }),
  );

export const commentTagApiResource = (injector: Injector) =>
  runInInjectionContext(
    injector,
    () =>
      new HttpResourceWithTrigger<IDropdownOption>({
        dependencies: [],
        formatter: (data: IDropdownOption[], { translate }): IDropdownOption[] => [
          ...specialCommentTags.map((tag) => ({ ...tag, name: translate.instant(tag.name) })),
          ...data,
        ],
        httpResourceRequest: {
          context: oeeContext,
          headers: widgetHttpHeader,
          method: 'GET',
          reportProgress: true,
          url: 'tags',
        },
        paramsUpdater: (request, { httpUtilities }, { manualParameters }): HttpResourceRequest => {
          return {
            ...request,
            params: httpUtilities.insertGenericCrudRequestParameters({
              filters: [{ field: 'objectType', ids: [3, 4] }],
              limit: 1000,
              search: { searchedFields: ['name'], searchText: manualParameters.search },
            }),
          };
        },
      }),
  );

export const customApiResource = (injector: Injector, customFilterConfiguration: IApiCustomFilterConfiguration) =>
  runInInjectionContext(
    injector,
    () =>
      new HttpResourceWithTrigger<unknown>({
        dependencies: [],
        formatter: (results, { filterCardService }): IDropdownOption<string>[] =>
          results.map((result) => {
            return {
              // @ts-ignore
              id: filterCardService.extractValueFromDataByWidgetRequest(
                [
                  {
                    columnNameOnWidget: 'a',
                    widgetRequestContentPath:
                      customFilterConfiguration.dropdownIdPath ?? customFilterConfiguration.dropdownNamePath,
                  },
                ],
                result,
              ).a,
              // @ts-ignore
              name: filterCardService.extractValueFromDataByWidgetRequest(
                [{ columnNameOnWidget: 'a', widgetRequestContentPath: customFilterConfiguration.dropdownNamePath }],
                result,
              ).a,
            };
          }),
        httpResourceRequest: {
          headers: { ...widgetHttpHeader },
          method: 'POST',
          url: `widget-requests/request/${customFilterConfiguration.widgetRequestId}`,
        },
      }),
  );
